// The button that hands you the file.
//
// A component of its own rather than three lines in the screen, because what it
// does is not rendering: it fetches a document nothing displays and gives it to
// the browser. Isolating it is also what lets the suite drive the fetch and the
// download separately from the page screen, which is about something else.
//
// A LAZY QUERY, and this is the whole reason `export` is on `PageQueries`
// rather than on `Page`. The bundle is every element and every shell override
// of every language of this page — the largest thing the API says about it —
// and no screen shows it. Selecting it as part of rendering the page would make
// every visit to this screen pay for a partition walk and a document nobody
// reads.
//
// `no-cache` and not merely `network-only`, which are different in the half that
// matters here. Both refetch; only `no-cache` also declines to WRITE the answer
// into the cache. A bundle is a SNAPSHOT — handing somebody a cached file for a
// page they have since edited is handing them a file that will overwrite their
// own edit when they import it back — and it is every element of every language
// of the page, sitting under the `pages/queries` entry that the whole app reads
// through, for a document nothing ever reads back.
import React, { useState } from "react";
import { Button } from "reactstrap";
import { useLazyQuery } from "@apollo/client";
import * as Notify from "@repoflow/form-schema/notify";
import * as PageQL from "queries/page";

import { bundleFilename, downloadBundle } from "components/bundle";

export const BUNDLE_PATH = "viewer.account.sites.get.pages.export";

// The error the model raises, in a sentence. The one refusal reachable from
// this button is a page that has gone — somebody removed it in another tab —
// and "page.export.notfound:9606858d" is not how to say so.
export const exportError = (error) => {
  const text = (error && error.toString()) || "";

  if (text.includes("page.export.notfound")) {
    return "That page no longer exists. Reload the listing.";
  }

  return text;
};

export const ExportButton = ({ siteId, page }) => {
  // Local, because it is about this button and not about the screen: a fetch in
  // flight disables the control that started it, and nothing else on the page
  // changes.
  const [busy, setBusy] = useState(false);

  const [run] = useLazyQuery(PageQL.exportBundle, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setBusy(false);
      const bundle = data && data.viewer.account.sites.get.pages.export;

      if (!bundle) {
        Notify.error("The server returned no bundle for that page.");
        return;
      }

      downloadBundle(bundle, bundleFilename(page));
    },
    onError: (error) => {
      setBusy(false);
      Notify.error(exportError(error));
    },
  });

  return (
    <Button
      color="secondary"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        run({ variables: { siteId, pageId: page.pageId } });
      }}
    >
      {busy ? "Exporting…" : "Export"}
    </Button>
  );
};
